import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Thermometer, Grid3X3, ChevronRight } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const services = [
  {
    icon: Thermometer,
    title: "Isolation",
    subtitle: "Thermique par l'Extérieur",
    description: "Une stratégie thermique globale pensée en cohérence avec l'implantation et l'orientation de votre bâtiment.",
    link: "/isolation",
    image: "https://images.unsplash.com/photo-1725862864518-1d95c2b32926"
  },
  {
    icon: Home,
    title: "Ossature Bois",
    subtitle: "Construction & Extension",
    description: "Le bois, matière naturelle et vivante au service de votre projet. Performance thermique et rapidité d'exécution.",
    link: "/ossature-bois",
    image: "https://images.pexels.com/photos/21378360/pexels-photo-21378360.jpeg"
  },
  {
    icon: Grid3X3,
    title: "Menuiserie",
    subtitle: "Intérieure & Extérieure",
    description: "L'organe respiratoire de votre bâtiment. Chaque ouverture influence la performance et le confort de votre lieu.",
    link: "/menuiserie",
    image: "https://images.pexels.com/photos/7109997/pexels-photo-7109997.jpeg"
  }
];

const values = [
  {
    title: "Cohérence",
    description: "Chaque décision est guidée par une question simple : est-ce que cet élément améliore réellement la qualité de vie dans le lieu ?"
  },
  {
    title: "Durabilité",
    description: "Nous construisons des lieux qui tiennent dans le temps. Un bâti qui consomme moins, qui vieillit bien et qui vous ressemble."
  },
  {
    title: "Harmonie",
    description: "Notre démarche s'appuie sur l'architecture bioclimatique, le bon sens constructif et la lecture des lieux."
  }
];

export const Accueil = () => {
  return (
    <div data-testid="accueil-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/382d9574-33cc-40d9-8eb7-2140f98201ed/images/dd99667ae3c6968f1889fb2a016fee3ea09056428bb8a9e0a57a69437b84e548.png"
            alt="Maison ossature bois"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/70 via-[#0a0908]/50 to-[#0a0908]"></div>
        </div>

        {/* Content */}
        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 text-center"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Rozier Menuiserie
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#fdfbf7] mb-8 leading-tight"
          >
            L'invisible structure<br />
            <span className="text-[#C5A059]">le visible</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base sm:text-lg text-[#a39c93] max-w-2xl mx-auto mb-12 leading-relaxed font-body"
          >
            Ossature bois, isolation thermique et menuiseries sur mesure. 
            Nous concevons des lieux vivants, pensés en cohérence avec leur environnement.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0a0908] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/ossature-bois"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#C5A059] text-[#C5A059] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#C5A059]/10 transition-colors duration-300"
            >
              Nos réalisations
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-[#C5A059] to-transparent"></div>
        </motion.div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 sm:py-32 lg:py-40 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Notre philosophie</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Concevoir, bâtir et aménager des{' '}
                <span className="text-[#C5A059]">lieux vivants</span>
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                Chez Rozier Menuiserie, le bien-être ne se limite pas au confort thermique ou à l'esthétique. 
                Nous concevons l'habitat comme un organisme vivant, en interaction permanente avec ses occupants, 
                son environnement et les flux naturels qui le traversent.
              </p>
              <p className="text-[#a39c93] leading-relaxed font-body">
                Chaque élément — structure, ouvertures, air, matières — joue un rôle précis dans l'équilibre du lieu.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_menuiserie-doree/artifacts/aesnwkuj_laurielinegood.jpeg"
                  alt="Laurie Line - Rozier Menuiserie"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 border border-[#C5A059]/30"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 sm:py-32 lg:py-40 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Nos expertises</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7]">
              Bâtis bois, isolation et menuiseries
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                data-testid={`service-card-${service.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
              >
                <Link 
                  to={service.link}
                  className="group block bg-[#1c1a17] border border-[#2a2622] hover:border-[#C5A059]/50 transition-all duration-500"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <service.icon size={20} className="text-[#C5A059]" />
                      <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059]">{service.subtitle}</p>
                    </div>
                    <h3 className="font-heading text-2xl text-[#fdfbf7] mb-3">{service.title}</h3>
                    <p className="text-[#a39c93] text-sm leading-relaxed mb-4 font-body">{service.description}</p>
                    <span className="inline-flex items-center gap-2 text-[#C5A059] text-sm group-hover:gap-3 transition-all">
                      En savoir plus <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 sm:py-32 lg:py-40 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Nos valeurs</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7]">
              Ce qui guide notre travail
            </h2>
          </motion.div>
          
          <motion.div 
            className="grid lg:grid-cols-3 gap-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {values.map((value, index) => (
              <motion.div 
                key={value.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                className="relative pl-8 border-l border-[#C5A059]/30"
              >
                <h3 className="font-heading text-2xl text-[#fdfbf7] mb-4">{value.title}</h3>
                <p className="text-[#a39c93] text-sm leading-relaxed font-body">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#1c1a17]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7] mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-[#a39c93] max-w-2xl mx-auto mb-8 font-body">
              Vous avez un projet de construction, de rénovation ou d'aménagement ? 
              Posons ensemble les bases d'un lieu cohérent, performant et durable.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0a0908] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
